<script setup lang="ts">
import {cx} from 'class-variance-authority'
import {useDedicatedTeam} from '~/composables/cms/dedicated-team'
import {lexicalToPlaintext} from '~/utils/lexical'
import {resolveRelations} from '~/utils/relations'
import Menu from '~/components/workshop/menu.vue'
import ReviewsSmall from '~/components/workshop/reviews-small.vue'
import CheckGreen from '~/assets/icons/check-green.svg?component'
import OtherSection from '~/components/workshop/other-services.vue'
import BlogSection from '~/components/workshop/blog-section.vue'
import HowWeWorkCard from '~/components/managed-projects/how-we-work-card.vue'
import BCard from '~/components/dedicated-team/b-card.vue'
import SingleTestimonial from '~/components/managed-projects/single-testimonial.vue'

const pixelGridLg = [
  '  111111111111111111111111111111111111111111111   ',
  ' 1111111111111111     111111211111111111111111    ',
  '  112221111111122111111111221211111111111111111   ',
  '       211111111111111111111111111111    2111111  ',
  '       211111111111111111111111111111    2111111  ',
  '                                    1    2111111  ',
  '       2111111                     11    2111111  ',
  '1111                     111111122111 221111111   ',
  '1111111111                  111111122111 221111111',
  '1111111111                                        ',
  '                            111111122111 221111111',
  '                            111111122111 221111111',
  '                                  122111 221111111',
  '                                      11 221111111',
].join('\n')

const {fetch} = useDedicatedTeam()

const {data: initialData} = await useAsyncData('dedicated-team', () => fetch())
const {data: dedicatedTeam} = useLivePreview({initialData: initialData.value, depth: 4})

if (!dedicatedTeam.value) {
  throw createError({status: 503, message: 'Could not load the page.'})
}

const lp = useLocalePath()
const {
  public: {baseUrl},
} = useRuntimeConfig()

const currentTab = ref<string>('Startup')
function onTabChange(option: string) {
  currentTab.value = option
}

useSchemaOrg([
  {
    '@type': 'FAQPage',
    mainEntity: computed(
      () =>
        resolveRelations(dedicatedTeam.value?.faq?.questions).map(item => ({
          '@type': 'Question',
          name: item.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: lexicalToPlaintext(item.body),
          },
        })) || []
    ),
  },
])

const seo = dedicatedTeam.value?.meta

useSeoMeta({
  title: seo?.title,
  description: seo?.description,
  ogUrl: `${baseUrl}/dedicated-team`,
  ogTitle: seo?.ogTitle || seo?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image),
  ogImageAlt: resolveMediaAlt(seo?.image),
})

const gradient = 'text-transparent bg-clip-text bg-gradient-to-b'
</script>

<template>
  <main v-if="dedicatedTeam" class="flex w-full flex-col overflow-hidden">
    <Container
      class="screen-content relative flex flex-col items-center space-y-6 pb-16 pt-32 md:pb-16 lg:pb-16 lg:pt-48"
    >
      <div class="mt-16 flex w-full flex-1 items-center justify-between">
        <div>
          <ReviewsSmall />

          <Heading size="display" as="h1" class="w-full text-center lg:text-left">
            <span :class="cx(gradient, 'from-gray-950 to-gray-600 pr-2 text-5xl lg:text-7xl')">
              {{ $t('dedicatedTeam.title.top') }}
            </span>
          </Heading>

          <Paragraph
            v-if="dedicatedTeam.hero"
            class="mt-4 w-full pb-8 text-center text-gray-600 lg:w-4/5 lg:text-left"
            size="medium"
            align="left"
          >
            {{ dedicatedTeam.hero.body }}
          </Paragraph>

          <Action
            class="lg:text-md flex w-full items-center justify-center py-3 text-sm font-normal md:w-28 lg:w-44"
            to="/contact"
            intent="main"
          >
            <NuxtLink href="/contact">{{ $t('dedicatedTeam.cta', 'Book free consultation') }}</NuxtLink></Action
          >
        </div>

        <Menu class="hidden md:block" @update-tab="onTabChange" :title="dedicatedTeam.menu?.title ?? undefined">
          <li
            class="text-md mb-4 flex list-none items-center gap-2 space-y-2 rounded-md border border-gray-300 p-3 text-gray-600"
            v-for="option in currentTab === 'Startup'
              ? dedicatedTeam.menu?.startupOptions
              : dedicatedTeam.menu?.options"
          >
            <CheckGreen class="h-6 w-6" />
            {{ typeof option === 'string' ? option : option.title }}
          </li>
        </Menu>
      </div>

      <span class="pt-24 text-gray-400">{{ $t('dedicatedTeam.heroParagraph') }}</span>

      <Partners :title="false" :partners="dedicatedTeam.testimonials?.partners ?? []" class="mt-4 px-8 pb-14 lg:px-0" />
      <div class="pointer-events-auto absolute inset-0 -z-10 h-full w-full px-4 md:px-8">
        <PixelGrid class="h-full" :pattern="pixelGridLg" />
      </div>
    </Container>

    <Container class="flex flex-col items-center justify-center sm:py-4 lg:py-12">
      <div class="mb-14 text-center">
        <Heading as="h2" size="caption">{{ dedicatedTeam.benefitsCards?.eyebrow }}</Heading>
        <Heading as="h3" size="large">{{ dedicatedTeam.benefitsCards?.title }}</Heading>
      </div>

      <div class="flex max-w-[85rem] flex-col gap-6 md:flex-row">
        <div class="mt-24 w-full flex-col md:w-1/2">
          <div class="mt-4" v-for="benefit in dedicatedTeam.benefitsCards?.benefits?.slice(0, 3)" :key="benefit.title">
            <BCard :data="benefit" />
          </div>
        </div>

        <div class="w-full flex-col md:w-1/2">
          <div class="mt-4" v-for="benefit in dedicatedTeam.benefitsCards?.benefits?.slice(3, 6)" :key="benefit.title">
            <BCard :data="benefit" />
          </div>
        </div>
      </div>
    </Container>

    <Container v-if="dedicatedTeam.singleTestimonial">
      <SingleTestimonial class="border-b-0 px-0 py-14 lg:px-80 lg:py-24" :data="dedicatedTeam.singleTestimonial" />
    </Container>

    <Container v-if="dedicatedTeam.howWeWork" class="screen-content px-8 py-16">
      <div class="mx-auto mb-6 flex w-full flex-col items-center justify-center text-center lg:w-1/2">
        <Heading as="h2" size="caption">{{ dedicatedTeam.howWeWork.eyebrow }}</Heading>
        <Heading as="h3" size="large" class="pb-8">{{ dedicatedTeam.howWeWork.title }}</Heading>

        <RichText :raw="dedicatedTeam.howWeWork.body" />
      </div>

      <div v-for="card in dedicatedTeam.howWeWork.infoCards?.slice(0, 2)" :key="card.number ?? undefined">
        <div class="relative flex items-center justify-center">
          <div
            v-if="card.video"
            class="absolute left-0 right-0 top-10 h-[0.2px] -translate-x-0 rotate-[-2deg] bg-gray-200"
          ></div>

          <HowWeWorkCard :card="card" />
        </div>
      </div>

      <span class="flex items-center justify-center pt-8 text-center">{{ $t('dedicatedTeam.or') }}</span>

      <div v-for="card in dedicatedTeam.howWeWork.infoCards?.slice(2, 8)" :key="card.number ?? undefined">
        <div class="relative flex items-center justify-center">
          <div
            v-if="card.video"
            class="absolute left-0 right-0 top-10 h-[0.2px] -translate-x-0 rotate-[-2deg] bg-gray-200"
          />
          <HowWeWorkCard :card="card" />
        </div>
      </div>

      <div class="mb-2 mt-14 flex w-full items-center justify-center">
        <Action intent="main" as="link" to="/contact">
          {{ $t('managed_projects.ctaLabel') }}
        </Action>
      </div>
    </Container>

    <Container v-if="dedicatedTeam.testimonials" class="screen-content flex flex-col items-center px-2 py-6">
      <Heading as="h2" size="caption">{{ dedicatedTeam.testimonials.eyebrow || '' }}</Heading>
      <Heading as="h3" size="large" class="mb-8 text-center">{{ dedicatedTeam.testimonials.title || '' }}</Heading>

      <Paragraph class="w-1/2 text-center">{{ dedicatedTeam.testimonials.body || '' }}</Paragraph>
      <Testimonials class="w-full" :testimonials="dedicatedTeam.testimonials.testimonials ?? []" />
    </Container>

    <Container v-if="dedicatedTeam.recruitment">
      <SingleTestimonial class="border-b-0 px-0 py-14 lg:px-80 lg:py-24" :data="dedicatedTeam.recruitment" />
    </Container>

    <Container
      v-if="dedicatedTeam.faq"
      class="screen-content flex flex-col items-center pb-16 pt-32 text-center md:pb-16 lg:pb-16 lg:pt-24"
    >
      <Heading size="caption" as="h2">{{ dedicatedTeam.faq.headline }}</Heading>
      <Heading as="h3" size="large" class="w-full lg:w-1/2">{{ dedicatedTeam.faq.title }}</Heading>
      <Paragraph class="mt-2 w-full pb-6 lg:w-1/2">{{ dedicatedTeam.faq.body }}</Paragraph>

      <Accordion
        v-if="dedicatedTeam.faq.questions"
        class="mt-8 pb-16 text-sm xs:px-12 xl:px-72"
        :items="dedicatedTeam.faq.questions"
      />
    </Container>

    <BlogSection v-if="dedicatedTeam.blog" :data="dedicatedTeam.blog" />

    <OtherSection v-if="dedicatedTeam.otherServices" :data="dedicatedTeam.otherServices" />

    <LazyCta class="pt-24" :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />
  </main>
</template>
